import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

/**
 * A mini donut chart for score display.
 */
const ScoreDonut = ({ score, size = 56, color = '#10E58F' }) => {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F8FAFC" strokeWidth={4} className="stroke-slate-100" />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={4} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[13px] font-black text-slate-900 tabular-nums">{score}</span>
      </div>
    </div>
  );
};

/**
 * Delta bar — visual comparison of a parameter between two interviews.
 */
const DeltaBar = ({ name, prevValue, currValue, maxValue = 100 }) => {
  const delta = currValue - prevValue;
  const isPositive = delta >= 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold text-slate-600 truncate max-w-[140px] tracking-wider">{name}</span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold text-slate-400 tabular-nums">{prevValue}</span>
          <Icon name="ArrowRight" size={10} className="text-slate-300" />
          <span className="text-[11px] font-black text-slate-800 tabular-nums">{currValue}</span>
          {delta !== 0 && (
            <span className={`text-[10px] font-black tabular-nums ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
              {isPositive ? '+' : ''}{delta}
            </span>
          )}
        </div>
      </div>
      <div className="h-1 bg-slate-50 rounded-full overflow-hidden relative">
        <div
          className="absolute top-0 h-full bg-slate-200 rounded-full transition-all duration-700"
          style={{ width: `${(prevValue / maxValue) * 100}%` }}
        />
        <div
          className={`absolute top-0 h-full rounded-full transition-all duration-1000 ${isPositive ? 'bg-emerald-500' : 'bg-rose-400'}`}
          style={{ width: `${(currValue / maxValue) * 100}%` }}
        />
      </div>
    </div>
  );
};

/**
 * MetricPill — small stat pill for key metrics.
 */
const MetricPill = ({ label, value, prevValue, icon, suffix = '' }) => {
  const delta = (prevValue !== undefined && value !== undefined) ? value - prevValue : 0;
  const isPositive = delta >= 0;
  const hasDelta = prevValue !== undefined && prevValue !== null && delta !== 0;

    return (
        <div className="flex items-center gap-2.5 px-3 py-2 bg-slate-50/50 rounded-xl border border-slate-100/50 transition-colors hover:bg-slate-50 min-w-0">
            <Icon name={icon} size={12} className="text-slate-400 shrink-0" />
            <div className="min-w-0 flex-1">
                <p className="text-[9px] font-black text-slate-400 tracking-wider truncate">{label}</p>
                <div className="flex items-center gap-1.5 overflow-hidden">
                    <span className="text-[12px] font-black text-slate-800 tabular-nums truncate">{value}{suffix}</span>
                    {hasDelta && (
                        <span className={`text-[9px] font-black tabular-nums shrink-0 ${isPositive ? 'text-emerald-500' : 'text-rose-500'}`}>
                            {isPositive ? '↑' : '↓'}{Math.abs(delta).toFixed(1).replace(/\.0$/, '')}{suffix}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Vertical comparison card showing two interviews side-by-side.
 */
const ComparisonCard = ({ current, previous, index, totalPairs, rank, totalInterviews }) => {
  const navigate = useNavigate();
  const cm = current?.computedMetrics || {};
  const pm = previous?.computedMetrics || {};

  const paramPairs = [];
  const currScores = current?.scores || [];
  const prevScores = previous?.scores || [];

  currScores.forEach(cp => {
    const pp = prevScores.find(p => p.name === cp.name);
    if (pp) {
      paramPairs.push({ name: cp.name, prev: pp.current, curr: cp.current });
    }
  });

  return (
    <div className="group relative">
      {/* Connector line between cards */}
      {index < totalPairs - 1 && (
        <div className="absolute left-[23px] top-full h-8 w-0.5 bg-slate-100 -z-10" />
      )}

      {/* Card numbering */}
      <div className="absolute -left-12 top-6 flex items-center gap-2">
        <span className="text-2xl font-black text-slate-300 group-hover:text-emerald-400/30 transition-colors">
          {totalInterviews ? (totalInterviews - index) : (totalPairs - index)}
        </span>
        <div className="w-4 h-0.5 bg-emerald-100 rounded-full" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 transition-all duration-300 hover:border-emerald-100 relative overflow-hidden">
        {/* Top-right corner badge */}
        {current?.is_challenge && (
          <div className="absolute top-0 right-0 px-3 py-1 bg-emerald-500 text-white rounded-bl-xl text-[8px] font-black tracking-[0.2em] shadow-sm z-20">
            <span className="flex items-center gap-1">
              <Icon name="Trophy" size={8} className="fill-current" />
              Challenge
            </span>
          </div>
        )}
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* Left panel: Info & Scores */}
          <div className="flex-1 space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex flex-col">
                  <span className={`text-[9px] font-black tracking-[0.2em] ${previous ? 'text-slate-400' : 'text-slate-300'}`}>Previous Session</span>
                  <span className="text-[11px] font-bold text-slate-600">{previous?.date || 'N/A'}</span>
                </div>
                <div className={`p-2 rounded-full ${previous ? 'bg-emerald-50 text-emerald-500' : 'bg-slate-50 text-slate-300 animate-pulse'}`}>
                  <Icon name="ArrowRight" size={14} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[9px] font-black text-emerald-600 tracking-[0.2em]">Latest Session</span>
                  <span className="text-[11px] font-bold text-slate-900">{current?.date}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => navigate(`/interview-report?session=${current.session_id}`)}
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl text-[10px] font-black tracking-widest hover:bg-emerald-600 transition-all flex items-center gap-2 h-[38px] whitespace-nowrap"
                >
                  View Report <Icon name="ExternalLink" size={12} />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-8 py-5 bg-slate-50/30 rounded-2xl border border-slate-100/50">
              <div className="flex-1 flex justify-center gap-10">
                {previous && (
                  <div className="text-center group/donut">
                    <ScoreDonut score={previous.overallScore || 0} color="#CBD5E1" />
                    <p className="text-[9px] font-black text-slate-400 tracking-widest mt-2">Before</p>
                  </div>
                )}
                <div className="flex flex-col items-center justify-center">
                  {(() => {
                    if (!previous) return (
                      <div className="flex flex-col items-center">
                        <span className="text-2xl font-black text-emerald-500 tabular-nums">
                          {current.overallScore}%
                        </span>
                        <span className="text-[9px] font-black text-emerald-500 tracking-widest mt-1">Starting Score</span>
                      </div>
                    );
                    const delta = (current?.overallScore || 0) - (previous.overallScore || 0);
                    return (
                      <>
                        <Icon
                          name={delta >= 0 ? 'ArrowUpCircle' : 'ArrowDownCircle'}
                          size={24}
                          className={delta >= 0 ? 'text-emerald-500' : 'text-rose-500'}
                        />
                        <span className={`text-xl font-black tabular-nums ${delta >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                          {delta >= 0 ? '+' : ''}{delta}
                        </span>
                      </>
                    );
                  })()}
                </div>
                <div className="text-center group/donut">
                  <ScoreDonut score={current?.overallScore || 0} color="#10E58F" />
                  <p className="text-[9px] font-black text-emerald-600 tracking-widest mt-2 font-black">
                    {previous ? 'After' : 'Current'}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <MetricPill label="Fillers" value={cm.fillerWordCount} prevValue={pm?.fillerWordCount} icon="Mic" />
              <MetricPill label="Talk Ratio" value={Math.round((cm.talkRatio || 0) * 100)} prevValue={pm ? Math.round((pm.talkRatio || 0) * 100) : null} icon="MessageSquare" suffix="%" />
              <MetricPill label="Pace (WPM)" value={Math.round(cm.wordsPerMinute || 0)} prevValue={pm ? Math.round(pm.wordsPerMinute || 0) : null} icon="Activity" />
              <MetricPill 
                label="Global Rank" 
                value={current?.is_challenge && !current?.results_released ? 'Pending' : (rank ? `#${rank}` : '—')} 
                icon="Globe" 
              />
            </div>
          </div>

          {/* Right panel: Skill Deltas (Desktop view only) */}
          <div className="w-full lg:w-64 space-y-4 pt-2">
            <h5 className="text-[10px] font-black text-slate-400 tracking-[0.2em] mb-4">Skill Progress</h5>
            <div className="space-y-4">
              {paramPairs.slice(0, 4).map((p, i) => (
                <DeltaBar key={i} name={p.name} prevValue={p.prev} currValue={p.curr} />
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

/**
 * Vertical list of interview comparisons.
 */
const InterviewComparisonList = ({ interviews, rank, hasMore, loadingMore, loadMore, totalInterviews }) => {
  const pairs = [];
  // Build pairs descending newest to oldest pairs
  for (let i = 0; i < interviews.length; i++) {
    pairs.push({ 
      current: interviews[i], 
      previous: i + 1 < interviews.length ? interviews[i + 1] : null 
    });
  }

  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [hasMore, loadingMore, loadMore]);

  if (pairs.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
        <Icon name="GitCompare" size={48} className="text-slate-100 mx-auto mb-4" />
        <h3 className="text-base font-bold text-slate-900 mb-1">Growth Tracking</h3>
        <p className="text-sm text-slate-400 max-w-xs mx-auto">
          Complete at least two interviews to see side-by-side performance comparisons.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-emerald-50 text-emerald-500">
          <Icon name="GitCompare" size={20} />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-900">Interview Journey</h3>
          <p className="text-xs text-slate-400 font-medium">Tracking your delta improvements over time</p>
        </div>
      </div>
      
      <div className="pl-12 space-y-8">
        {pairs.map((pair, idx) => (
          <ComparisonCard 
            key={idx} 
            current={pair.current} 
            previous={pair.previous} 
            index={idx}
            totalPairs={pairs.length}
            rank={rank}
            totalInterviews={totalInterviews}
          />
        ))}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="py-8 flex justify-center pl-12">
          {loadingMore && (
            <div className="flex items-center gap-2 text-slate-400 font-bold text-[11px] tracking-widest bg-slate-50/50 px-5 py-2.5 rounded-full border border-slate-100/50">
              <svg className="animate-spin h-4 w-4 text-emerald-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Loading older sessions...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InterviewComparisonList;
